/**
 * A QR payment as the back office sees it.
 *
 * Statuses that matter on this side. `PENDING` means nobody has asked the bank yet;
 * `AWAITING_SETTLEMENT` means the salesperson closed the sale knowing the money had not
 * arrived — the customer said they would transfer later — which is the row back office
 * exists to chase. Both are still checkable. Everything else is final.
 */
export type QrPaymentStatus =
  | "PENDING"
  | "PAID"
  | "EXPIRED"
  | "FAILED"
  | "CANCELLED"
  | "AMOUNT_MISMATCH"
  | "REFUNDED"
  | "DUPLICATE_PAYMENT"
  | "AWAITING_SETTLEMENT";

/** `GET /qr-payments/bill/:bill_uuid` and `POST /qr-payments/:uuid/verify`. */
export interface QrPayment {
  uuid: string;
  status: QrPaymentStatus;
  /**
   * The bank's own QR image, base64. Render this rather than drawing `qr_raw_data` — SCB
   * requires a displayed QR30 to carry the PromptPay mark, and their image already does.
   */
  qr_image: string | null;
  /** EMVCo Thai QR30 payload. A fallback only; prefer `qr_image`. */
  qr_raw_data: string;
  amount: string;
  ref1: string;
  expires_at: string | null;
  created_at: string;
  /**
   * What the bank says was actually transferred — not a copy of `amount`. Null until a
   * check has found the money.
   */
  paid_amount: string | null;
  paid_at: string | null;
  payer_name: string | null;
  last_verified_at: string | null;
  verify_attempts: number;
  settlement_reason: string | null;
  settlement_marked_by: string | null;
  settlement_marked_at: string | null;
  settlement_note: string | null;
}

/**
 * One row of `GET /qr-payments/unclaimed` — money the bank has confirmed that no bill
 * claims. Deliberately narrower than `QrPayment`: no `qr_image`, which is a base64 PNG
 * and on a list is megabytes nobody renders.
 */
export interface UnclaimedQrPayment {
  uuid: string;
  amount: string;
  paid_amount: string | null;
  paid_at: string | null;
  payer_name: string | null;
  sending_bank_code: string | null;
  scb_slip_number: string | null;
  ref1: string;
  branch_code: string | null;
  visit_uuid: string | null;
  customer_uuid: string | null;
  created_at: string;
}

export interface UnclaimedQrPaymentResponse {
  rows: UnclaimedQrPayment[];
  count: number;
}

/** What the bank has confirmed transferred, falling back to what the QR was raised for. */
export const settledAmountOf = (qr: {
  paid_amount: string | null;
  amount: string;
}): number => Number(qr.paid_amount ?? qr.amount);

/**
 * Money is in the bank against this reference, so the bill's channel can no longer move.
 */
export const isSettled = (status: QrPaymentStatus): boolean => status === "PAID";

/**
 * Still worth asking the bank about. Mirrors the API's re-verifiable set, plus CANCELLED.
 *
 * CANCELLED is checkable and is NOT a contradiction: SCB has no way to withdraw an
 * unexpired QR, so a code outlives the sale that closed it and can be paid afterwards. The
 * API answers an inquiry on such a row by recording the bank's figures and deliberately
 * keeping the CANCELLED status, which is exactly what makes the check worth offering —
 * without it, a QR closed when the salesperson switched the bill to โอน could never be
 * shown to hold the customer's money.
 */
export const isCheckable = (status: QrPaymentStatus): boolean =>
  status === "PENDING" ||
  status === "AWAITING_SETTLEMENT" ||
  status === "CANCELLED";

/**
 * Whether the bank's own figures say this QR holds money, whatever its status reads.
 *
 * The second half is the whole point. A QR closed alongside a sale that moved to another
 * channel keeps CANCELLED through every later inquiry, on purpose, so it stays in the queue
 * of money nobody has accounted for — the payment is recorded beside the status rather than
 * in it. `paid_at` is written only by a check that actually found the transfer, so it is
 * the honest test for "the bank confirmed this", and `paid_amount` has to be there too
 * because it is what the API compares against the bill.
 *
 * Mirrors the API's own rule, which refuses to attach a QR that fails it.
 */
export const holdsConfirmedMoney = (qr: {
  status: QrPaymentStatus;
  paid_amount: string | null;
  paid_at: string | null;
}): boolean => {
  if (qr.status === "PAID") return true;
  if (qr.status !== "CANCELLED") return false;
  return qr.paid_at !== null && qr.paid_amount !== null;
};

export const QR_PAYMENT_STATUS_LABEL: Record<QrPaymentStatus, string> = {
  PENDING: "ยังไม่พบเงินเข้า",
  PAID: "เงินเข้าแล้ว",
  EXPIRED: "หมดอายุ",
  FAILED: "ล้มเหลว",
  CANCELLED: "ยกเลิก",
  AMOUNT_MISMATCH: "ยอดไม่ตรง",
  REFUNDED: "คืนเงินแล้ว",
  DUPLICATE_PAYMENT: "ชำระซ้ำ",
  AWAITING_SETTLEMENT: "รอลูกค้าโอน",
};

export const getQrStatusLabel = (status: QrPaymentStatus): string =>
  QR_PAYMENT_STATUS_LABEL[status] ?? status;

/**
 * The three work queues on the QR Transaction screen.
 *
 * The first two are money the bank has that the books do not account for, and neither is
 * reachable from any bill-first screen: one has no bill to start from, the other's bill was
 * cancelled. The third is money that HAS been accounted for and is on its way back —
 * ordered, not yet confirmed — which is the gap between our figures and the bank statement
 * for as long as it lasts.
 */
export type QrPaymentQueue = "UNCLAIMED" | "PAID_AFTER_CANCEL" | "REFUND_REQUESTED";

/**
 * `GET /qr-payments/refund-outstanding` — money ordered back that has not gone back yet.
 *
 * The total is a string for the same reason every other amount in this domain is: it is a
 * sum over money, and a large one must not lose satang on its way through a JS number.
 */
export interface QrRefundOutstanding {
  count: number;
  total_amount: string;
}

/** One row of `GET /qr-payments`. No `qr_image` / `qr_raw_data` — see the detail endpoint. */
export interface QrPaymentListItem {
  uuid: string;
  status: QrPaymentStatus;
  amount: string;
  paid_amount: string | null;
  ref1: string;
  ref2: string | null;
  branch_code: string | null;
  bill_uuid: string | null;
  visit_uuid: string | null;
  customer_uuid: string | null;
  expires_at: string | null;
  paid_at: string | null;
  payer_name: string | null;
  scb_slip_number: string | null;
  /**
   * When the bank told us this QR was paid.
   *
   * On a CANCELLED row this is the ONLY report that money arrived: such a row never takes
   * the PAID status, because the sale it belonged to was called off before the payment
   * landed. See `isPaidAfterCancel`.
   */
  confirmation_received_at: string | null;
  last_verified_at: string | null;
  verify_attempts: number;
  settlement_reason: string | null;
  /**
   * Set once the money has been sent back, and reused verbatim by the manual flow: a refund
   * accounting performed at the bank writes the same three columns as the old SCB reversal
   * did, so a row refunded last month and a row refunded today read identically here.
   * `voided_by` is the username who confirmed it.
   */
  voided_at: string | null;
  voided_by: string | null;
  /**
   * When someone ordered this money sent back — NOT when it was sent.
   *
   * The two are days apart by design: the platform no longer moves money at all, so this is
   * a work order for accounting, who perform the refund in the bank's own system and come
   * back to confirm it. One-way: the server refuses a second order on the same row, and
   * refuses to attach the money to any bill once it is set.
   */
  refund_requested_at: string | null;
  refund_requested_by: string | null;
  refund_reason: string | null;
  created_at: string;
}

export interface QrPaymentListResponse {
  rows: QrPaymentListItem[];
  count: number;
}

/** Confirmed money that no bill claims. */
export const isUnclaimed = (row: QrPaymentListItem): boolean =>
  row.status === "PAID" && !row.bill_uuid;

/**
 * The sale was cancelled and the customer paid anyway.
 *
 * Worse than unclaimed money, and shown as such: unclaimed money is waiting to be matched to
 * a bill that may well exist, while this money has no sale left to belong to and has to go
 * back to the person who sent it.
 */
export const isPaidAfterCancel = (row: QrPaymentListItem): boolean =>
  row.status === "CANCELLED" && Boolean(row.confirmation_received_at);

/**
 * The bank has reported this payment, but its figures never landed on the row.
 *
 * SCB's confirmation callback and its inquiry read two different stores, and the inquiry's
 * is behind: an inquiry fired in the same second as the callback can come back "no such
 * record", which is also what an unpaid reference looks like. The row then keeps its
 * `paid_amount` empty next to a `confirmation_received_at` that says money arrived — which
 * is exactly the "มีเวลาเงินเข้า แต่ไม่มียอด" a back-office user reads as a broken screen.
 *
 * The server now retries for a few seconds behind the callback, so this should be rare.
 * What survives that is what this marks: a row for a person to re-ask about, rather than a
 * dead end nobody can act on. `paid_at` and not `status`, because a cancelled-but-paid row
 * keeps its status and takes only the figures.
 */
export const needsRecheck = (row: QrPaymentListItem): boolean =>
  Boolean(row.confirmation_received_at) && !row.paid_at && !row.voided_at;

/**
 * Why this row cannot be handed a replacement code, or `null` when it can.
 *
 * The case this exists for: a salesperson closed a sale as "the customer will transfer
 * later", the customer kept the QR, and it expired minutes after the van left. Weeks on,
 * finance rings them and is told the code no longer works — which is true, and until now
 * left nothing anyone could do about it.
 *
 * Mirrors the server's preconditions so the row can say why before anyone presses. A QR the
 * customer can STILL scan is deliberately refused: two payable codes against one bill is two
 * payments to reconcile against one document. Rows raised before QRs carried expiries have
 * no `expires_at` and never die, so they land in that same refusal — correctly, since that
 * customer can still pay the code they already hold.
 */
export const reissueBlockedReason = (row: QrPaymentListItem): string | null => {
  if (row.status !== "AWAITING_SETTLEMENT") {
    return "ออกใหม่ได้เฉพาะบิลที่ปิดแบบ “ลูกค้าจะโอนภายหลัง”";
  }
  if (!row.bill_uuid) return "รายการนี้ไม่มีบิล จึงออก QR ใหม่ไม่ได้";
  if (!row.expires_at) return "QR ใบนี้ไม่มีวันหมดอายุ ลูกค้ายังจ่ายใบเดิมได้";
  if (new Date(row.expires_at).getTime() > Date.now()) {
    return "QR ใบนี้ยังไม่หมดอายุ ลูกค้ายังจ่ายใบเดิมได้";
  }
  return null;
};

/**
 * Statuses on which a refund may be ordered.
 *
 * Wider than the set the old SCB reversal accepted, and deliberately so. That one was narrow
 * for two reasons that have both gone: a reversal had to be keyed on a `scb_transaction_id`
 * the bank had reported, and SCB refused one past the day's cut-off. Ordering a refund asks
 * the bank for nothing, so the only question left is whether money was ever reported against
 * this QR.
 *
 * DUPLICATE_PAYMENT and AMOUNT_MISMATCH are in for the same reason: they mean MORE money
 * arrived than the bill is for, which is exactly what has to go back, and both were dead ends
 * before.
 */
const REFUNDABLE_STATUSES: ReadonlySet<QrPaymentStatus> = new Set<QrPaymentStatus>([
  "PAID",
  "CANCELLED",
  "AMOUNT_MISMATCH",
  "DUPLICATE_PAYMENT",
]);

/** Ordered to be sent back, but accounting has not confirmed it yet. */
export const isAwaitingRefund = (row: QrPaymentListItem): boolean =>
  Boolean(row.refund_requested_at) && row.status !== "REFUNDED";

/**
 * Why this row cannot be ordered for refund, or `null` when it can.
 *
 * Mirrors the server's preconditions so the screen can say what is wrong before anyone
 * presses anything. Nothing here moves money any more — the order is a note for accounting,
 * who refund it in the bank's own system — but it is still one-way, which is why the row says
 * so before the press rather than after.
 *
 * The CANCELLED case carries an extra test the server does not: a sale called off before
 * anyone paid leaves an ordinary CANCELLED row with no money behind it, and ordering a refund
 * on one would send accounting looking for a transfer that never happened. The bank's own
 * report is the evidence, and on such a row it lands in `confirmation_received_at` rather
 * than `paid_at`, because the status never moves to PAID.
 */
export const refundBlockedReason = (row: QrPaymentListItem): string | null => {
  if (row.status === "REFUNDED" || row.voided_at) return "คืนเงินไปแล้ว";
  if (row.refund_requested_at) return "สั่งคืนเงินไปแล้ว รอบัญชียืนยัน";
  if (!REFUNDABLE_STATUSES.has(row.status)) return "ยังไม่มีเงินเข้า จึงไม่มีอะไรให้คืน";
  if (
    row.status === "CANCELLED" &&
    !row.paid_at &&
    !row.paid_amount &&
    !row.confirmation_received_at
  ) {
    return "รายการนี้ยกเลิกโดยไม่มีเงินเข้า จึงไม่มีอะไรให้คืน";
  }
  return null;
};

export const canRequestRefund = (row: QrPaymentListItem): boolean =>
  refundBlockedReason(row) === null;

/**
 * Why this row's refund cannot be confirmed, or `null` when it can.
 *
 * Confirming is the terminal act: nothing in the platform ever re-reads a REFUNDED row, so
 * whoever presses this is the last check there will be. It is therefore only offered on a row
 * where the order exists — the person confirming should be closing a job someone raised, not
 * declaring one out of nowhere.
 */
export const refundConfirmBlockedReason = (row: QrPaymentListItem): string | null => {
  if (row.status === "REFUNDED" || row.voided_at) return "คืนเงินไปแล้ว";
  if (!row.refund_requested_at) return "ต้องกด “สั่งคืนเงิน” ก่อน";
  return null;
};
